import { Controller } from "@hotwired/stimulus"
import { es, en } from "intl-tel-input/i18n";
import intlTelInput from "intl-tel-input"


export default class extends Controller {
  static targets = ["display", "hidden", "error"]
  static values = {
    locale: String,
    defaultCountry: String
  }

  connect() {
    this.initializePhoneInput()
    this.handleResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.handleResize)
    this.showErrorIfExists()
  }

  disconnect() {
    if (this.iti) {
      this.iti.destroy()
    }
    window.removeEventListener('resize', this.handleResize)
  }

  showErrorIfExists() {
    const errorMessage = this.hiddenTarget.dataset.error
    if (errorMessage) {
      this.displayTarget.classList.add('border-red-500')
      this.errorTarget.classList.remove('hidden')
      this.errorTarget.textContent = this.getErrorMessage(errorMessage)
    }
  }

  getErrorMessage(error) {
    const messages = {
      en: {
        invalid: "Please enter a valid phone number",
        blank: "Phone number can't be blank",
        taken: "This phone number is already registered"
      },
      es: {
        invalid: "Por favor, introduce un número de teléfono válido",
        blank: "El número de teléfono no puede estar vacío",
        taken: "Este número de teléfono ya está registrado"
      }
    }

    const locale = this.localeValue || 'en'
    const messageSet = messages[locale]
    return messageSet[error.toLowerCase()] || error
  }

  handleResize() {
    const countryList = document.querySelector('.iti__country-list')
    if (countryList) {
      this.positionCountryList(countryList)
    }
  }

  positionCountryList(countryList) {
    const isMobile = window.innerWidth < 768
    if (isMobile) {
      countryList.style.position = 'fixed'
      countryList.style.bottom = '80px'
      countryList.style.left = '1rem'
      countryList.style.right = '1rem'
      countryList.style.width = 'auto'
      countryList.style.maxHeight = '40vh'
    } else {
      countryList.style.position = 'absolute'
      countryList.style.bottom = 'auto'
      countryList.style.left = '0'
      countryList.style.right = 'auto'
      countryList.style.width = '100%'
      countryList.style.maxHeight = '200px'
    }
  }

  initializePhoneInput() {
    const locale = this.localeValue || 'en'
    const i18nTranslations = locale === 'es' ? es : en

    this.iti = intlTelInput(this.displayTarget, {
      useFullscreenPopup: false,
      initialCountry: this.defaultCountryValue || "es",
      preferredCountries: ["es", "us", "gb"],
      separateDialCode: false,
      loadUtils: () => import("intl-tel-input/build/js/utils.js"),
      language: locale,
      i18n: i18nTranslations,
      validateOnBlur: true,
    })

    setTimeout(() => {
      this.parseExistingNumber()
    }, 150)

    this.parseExistingNumber()

    this.displayTarget.addEventListener('input', () => {
      this.cleanInputValue()
      this.updateHiddenInput()
      this.validateAndShowError()
    })

    this.displayTarget.addEventListener('countrychange', () => {
      this.cleanInputValue()
      this.updateHiddenInput()
      this.validateAndShowError()
    })

    this.displayTarget.closest('form').addEventListener('submit', (e) => {
      if (!this.validate()) {
        e.preventDefault()
        this.validateAndShowError()
        return false
      }
      this.updateHiddenInput()
    })
  }

  validateAndShowError() {
    if (!this.validate()) {
      this.errorTarget.classList.remove('hidden')
      this.errorTarget.textContent = this.getErrorMessage('invalid')
    } else {
      this.errorTarget.classList.add('hidden')
      this.errorTarget.textContent = ''
    }
  }

  validate() {
    if (this.iti.isValidNumber()) {
      this.displayTarget.classList.remove('border-red-500')
      return true
    } else {
      this.displayTarget.classList.add('border-red-500')
      return false
    }
  }

  parseExistingNumber() {
    const existingValue = this.hiddenTarget.value || this.displayTarget.value

    if (existingValue && existingValue.startsWith('+')) {
      try {
        const countryCode = existingValue.match(/^\+(\d+)/)[1]
        let nationalNumber = existingValue.replace(/^\+\d+\s*/, '')

        if (nationalNumber.startsWith(countryCode)) {
          nationalNumber = nationalNumber.substring(countryCode.length)
        }

        this.iti.setCountry(this.getCountryCodeByDialCode(countryCode))
        this.displayTarget.value = nationalNumber.trim()
        this.updateHiddenInput()
        this.displayTarget.classList.remove('border-red-500')
        if (this.errorTarget) {
          this.errorTarget.classList.add('hidden')
          this.errorTarget.textContent = ''
        }
      } catch (e) {
        console.error('Error parsing phone number:', e)
      }
    }
  }

  cleanInputValue() {
    if (this.displayTarget.value) {
      let value = this.displayTarget.value
      const selectedCountry = this.iti.getSelectedCountryData()

      if (selectedCountry && selectedCountry.dialCode) {
        const dialCode = selectedCountry.dialCode
        value = value.replace(new RegExp(`^\\+?${dialCode}\\s*`), '')
        value = value.replace(new RegExp(`^${dialCode}\\s*`), '')
      }
      value = value.replace(/^\+/, '')
      this.displayTarget.value = value.trim()
    }
  }

  getCountryCodeByDialCode(dialCode) {
    const allCountries = this.iti.getCountryData()
    const country = allCountries.find(c => c.dialCode === dialCode)
    return country ? country.iso2 : 'es'
  }

  updateHiddenInput() {
    if (this.iti) {
      const fullNumber = this.iti.getNumber()
      this.hiddenTarget.value = fullNumber
    }
  }
}
